import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedSection } from "@/src/common/components/animated-section";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { getStringArray } from "@/src/common/utils/i18n.utils";
import { PricingFaq } from "@/src/pricing/components/pricing-faq";
import { PricingTierCard, type PricingTone } from "@/src/pricing/components/pricing-tier-card";

type Billing = "monthly" | "annual";

interface TierConfig {
	id: "free" | "pro" | "premium";
	tone: PricingTone;
}

const TIERS: ReadonlyArray<TierConfig> = [
	{ id: "free", tone: "default" },
	{ id: "pro", tone: "highlighted" },
	{ id: "premium", tone: "premium" },
];

export function PricingPage() {
	const { t } = useTranslation("pricing");
	const [billing, setBilling] = useState<Billing>("monthly");

	return (
		<>
			<PageHelmet title={t("meta.title")} description={t("meta.description")} />

			<section className="bg-mesh relative isolate overflow-hidden pt-20 pb-12 md:pt-32 md:pb-16">
				<div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
					<SectionHeading
						as="h1"
						eyebrow={<EyebrowBadge tone="ink">{t("hero.eyebrow")}</EyebrowBadge>}
						title={t("hero.title")}
						titleAccent={t("hero.title_accent")}
						description={t("hero.subtitle")}
					/>
				</div>
			</section>

			<div className="mx-auto -mt-2 flex max-w-3xl justify-center px-4 sm:px-6 lg:px-8">
				<div className="inline-flex items-center rounded-full border border-default-200 bg-white p-1 shadow-card">
					{(["monthly", "annual"] as const).map((value) => {
						const isActive = billing === value;
						return (
							<button
								key={value}
								type="button"
								onClick={() => setBilling(value)}
								aria-pressed={isActive}
								className={`relative rounded-full px-4 py-1.5 text-sm font-semibold transition ${
									isActive ? "bg-brand-gradient text-white shadow-sm" : "text-default-600 hover:text-foreground"
								}`}
							>
								{t(`billing.${value}`)}
								{value === "annual" ? (
									<span className="ml-2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
										{t("billing.save")}
									</span>
								) : null}
							</button>
						);
					})}
				</div>
			</div>

			<section className="pt-12 pb-24 md:pt-16 md:pb-32">
				<div className="mx-auto grid max-w-7xl gap-6 px-4 md:grid-cols-3 sm:px-6 lg:px-8">
					{TIERS.map(({ id, tone }, index) => {
						const features = getStringArray(t(`tiers.${id}.features`, { returnObjects: true }));
						const badge = id === "pro" ? t("tiers.pro.badge") : undefined;
						const priceKey = id === "pro" ? `tiers.pro.price_${billing}` : `tiers.${id}.price`;
						const periodKey = id === "pro" ? "tiers.pro.period" : id === "free" ? "tiers.free.period" : undefined;
						return (
							<Reveal key={id} delay={index * 0.1} className="h-full">
								<PricingTierCard
									id={id}
									tone={tone}
									name={t(`tiers.${id}.name`)}
									tagline={t(`tiers.${id}.tagline`)}
									price={t(priceKey)}
									period={periodKey ? t(periodKey) : undefined}
									description={t(`tiers.${id}.description`)}
									features={features}
									cta={t(`tiers.${id}.cta`)}
									footnote={t(`tiers.${id}.footnote`)}
									badge={badge}
								/>
							</Reveal>
						);
					})}
				</div>
			</section>

			<AnimatedSection className="border-t border-default-200/60 bg-content1/40 py-24 md:py-32">
				<div className="mx-auto px-4 sm:px-6 lg:px-8">
					<PricingFaq />
				</div>
			</AnimatedSection>
		</>
	);
}
